import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { announceCartDiscountUpdate, settleWithCart } from '@theme/standard-cart-events';

class CartDiscountComponent extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping', 'button'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    // Abort any pending fetch
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    /** @type {ReturnType<typeof announceCartDiscountUpdate> | null} */
    let announcement = null;

    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    this.refs.button.classList.add('btn--loading');
    this.refs.button.disabled = true;

    const controller = new AbortController();
    this.#activeFetch?.abort();
    this.#activeFetch = controller;

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      announcement = announceCartDiscountUpdate(this, [...existingDiscounts, discountCodeValue]);

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: controller.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        if (announcement) settleWithCart(announcement, data, { didError: true, source: 'cart-discount-component' });
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart__discounts .discount__item-code') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element?.dataset?.discountCode))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          if (announcement) settleWithCart(announcement, data, { didError: true, source: 'cart-discount-component' });
          return;
        }
      }

      if (announcement) settleWithCart(announcement, data, { source: 'cart-discount-component' });
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id, { sections: data.sections }));
      morphSection(this.dataset.sectionId, newHtml).then(() => this.#closeEnclosingDialog());

    } catch (error) {
      announcement?.reject(error);
      if (error?.name !== 'AbortError') console.error('[cart-discount] apply failed', error);
    } finally {
      if (this.#activeFetch === controller) {
        this.#activeFetch = null;
        this.refs.button.classList.remove('btn--loading');
        this.refs.button.disabled = false;
      }
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    /** @type {ReturnType<typeof announceCartDiscountUpdate> | null} */
    let announcement = null;

    event.preventDefault();
    event.stopPropagation();

    // The on:click handler only ever receives the (proxied) click event whose
    // target is the bound button, so an explicit event-type check isn't needed.
    if (!(event.target instanceof HTMLElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountButton = event.target;
    const discountCode = discountButton.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    discountButton.classList.add('loading-state');
    const controller = new AbortController();
    this.#activeFetch?.abort();
    this.#activeFetch = controller;

    announcement = announceCartDiscountUpdate(this, existingDiscounts);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: controller.signal,
      });

      const data = await response.json();

      settleWithCart(announcement, data, { source: 'cart-discount-component' });
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id, { sections: data.sections }));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      announcement?.reject(error);
      if (error?.name !== 'AbortError') console.error('[cart-discount] remove failed', error);
    } finally {
      discountButton.classList.remove('loading-state');
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  };

  // Dismisses the dialog holding the form once the morph lands, leaving the cart drawer behind it open. No-op on the cart page, where the form sits outside any dialog.
  #closeEnclosingDialog() {
    const dialog = this.closest('.dialog-component');
    if (dialog && typeof dialog.closeDialog === 'function') dialog.closeDialog();
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.cart?.querySelectorAll('.discount__item-code') ?? [];
    for (const pill of discountPills) {
      const discountCode = pill?.dataset?.discountCode;
      if (!discountCode || typeof discountCode !== 'string') continue;
      if (discountCodes.includes(discountCode)) continue;
      discountCodes.push(discountCode);
    }

    return discountCodes;
  }

  get cart() {
    return document.querySelector('cart-items-component');
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscountComponent);
}